import {
  Box,
  Button,
  Flex,
  HStack,
  Input,
  PreHeader,
  Stack,
  Text,
} from "components/ui";
import { SetupGuideLink } from "components/common/SetupGuideLink";
import { useTranslation } from "hooks/useTranslation";
import { useState } from "react";
import { integrationService } from "services/integrationService";

interface IntercomTokenFormProps {
  onConnected: () => void;
  onClose: () => void;
}

/**
 * Connects Intercom with an access token of the tenant's own Intercom app. Connecting the same
 * Intercom workspace again updates its existing integration. The optional client secret verifies
 * the webhooks that app sends; left blank, a reconnected integration keeps the stored one.
 */
const IntercomTokenForm = ({
  onConnected,
  onClose,
}: IntercomTokenFormProps) => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [accessToken, setAccessToken] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState("");

  const handleConnect = async () => {
    if (!accessToken.trim()) return;
    try {
      setConnecting(true);
      setError("");
      await integrationService.connectIntercomWithToken(
        accessToken.trim(),
        name.trim() || undefined,
        clientSecret.trim() || undefined,
      );
      onConnected();
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          t("app.supportIntegrations.intercomToken.failed"),
      );
    } finally {
      setConnecting(false);
    }
  };

  const label = (text: string) => (
    <Text
      fontSize="sm"
      mb={1}
      fontWeight={500}
      color="var(--text-primary)"
      m={0}
    >
      {text}
    </Text>
  );

  const hint = (text: string) => (
    <Text fontSize="xs" mt={1} color="var(--text-secondary-color)" m={0}>
      {text}
    </Text>
  );

  return (
    <Box
      p={4}
      borderRadius="10px"
      borderWidth="1px"
      borderColor="var(--brand-primary, #3B82F6)"
      bg="rgba(59, 130, 246, 0.06)"
    >
      <Box mb={3}>
        <PreHeader size="xs">Intercom</PreHeader>
      </Box>
      <Flex justifyContent="space-between" alignItems="center" gap={3} mb={3}>
        <Text fontWeight={500} color="var(--text-primary)" m={0}>
          {t("app.supportIntegrations.intercomToken.title")}
        </Text>
        <SetupGuideLink
          doc="support-integrations-setup"
          section="intercom"
          intent="ghost"
          label={t("app.common.whereDoIFindThese")}
        />
      </Flex>
      <Stack gap={3}>
        <Box>
          {label(t("app.supportIntegrations.intercomToken.name"))}
          <Input
            placeholder={t(
              "app.supportIntegrations.intercomToken.namePlaceholder",
            )}
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxW="360px"
          />
        </Box>
        <Box>
          {label(t("app.supportIntegrations.intercomToken.accessToken"))}
          <Input
            placeholder={t(
              "app.supportIntegrations.intercomToken.accessTokenPlaceholder",
            )}
            value={accessToken}
            onChange={(e) => setAccessToken(e.target.value)}
            maxW="360px"
            type="password"
            autoComplete="new-password"
          />
          {hint(t("app.supportIntegrations.intercomToken.accessTokenHelp"))}
        </Box>
        <Box>
          {label(t("app.supportIntegrations.intercomToken.clientSecret"))}
          <Input
            placeholder={t(
              "app.supportIntegrations.intercomToken.clientSecretPlaceholder",
            )}
            value={clientSecret}
            onChange={(e) => setClientSecret(e.target.value)}
            maxW="360px"
            type="password"
            autoComplete="new-password"
          />
          {hint(t("app.supportIntegrations.intercomToken.clientSecretHelp"))}
        </Box>
        {error && (
          <Text fontSize="sm" color="var(--accent-danger, #EF4444)" m={0}>
            {error}
          </Text>
        )}
        <HStack gap={2}>
          <Button
            intent="primary"
            onClick={handleConnect}
            disabled={connecting || !accessToken.trim()}
          >
            {connecting
              ? t("app.supportIntegrations.connecting")
              : t("app.supportIntegrations.intercomToken.connect")}
          </Button>
          <Button intent="ghost" onClick={onClose} disabled={connecting}>
            {t("app.common.cancel")}
          </Button>
        </HStack>
      </Stack>
    </Box>
  );
};

export default IntercomTokenForm;
